import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { ASSESSMENT } from '@/constants/testIds';
import { ArrowLeft } from 'lucide-react';

const Intro = ({ onStart, loading, lang, setLang, concern, setConcern }) => (
  <div className="sans-container max-w-3xl sans-section">
    <div className="sans-label mb-6">Free • Classical Ayurveda</div>
    <h1 className="sans-h1">Prakriti Assessment</h1>
    <div className="sans-rule mt-8" />
    <p className="mt-10 text-lg text-sans_ink leading-relaxed">
      About five minutes. Fifteen questions rooted in classical Ayurvedic principles — covering body, digestion,
      sleep, energy, temperament, and lifestyle.
    </p>
    <p className="mt-4 text-sans_mute leading-relaxed">
      An educational estimate of your constitution (Prakriti), not a medical diagnosis. At the end, you'll receive a
      personal report and, if you wish, the option to speak with a qualified Ayurvedic doctor.
    </p>

    <div className="mt-12">
      <label className="sans-label mb-3 block">What brings you here?</label>
      <textarea
        rows={4}
        value={concern}
        onChange={(e) => setConcern(e.target.value)}
        placeholder="A few lines about what you'd like to understand or improve — sleep, digestion, weight, stress, skin, cycles, energy…"
        data-testid="assessment-concern-textarea"
        className="w-full border border-sans_line bg-white p-4 text-sans_ink leading-relaxed focus:outline-none focus:border-sans_ink transition-colors"
      />
      <div className="mt-2 text-xs text-sans_mute">Optional but recommended — helps the report focus on what matters to you.</div>
    </div>

    <div className="mt-10">
      <div className="sans-label mb-3">Language</div>
      <div className="inline-flex border border-sans_line">
        <button type="button" onClick={() => setLang('en')} data-testid="lang-en"
          className={`px-6 py-2.5 text-sm tracking-wide transition-colors ${lang === 'en' ? 'bg-sans_ink text-sans_bone' : 'bg-white text-sans_ink hover:bg-sans_bone'}`}>English</button>
        <button type="button" onClick={() => setLang('hi')} data-testid="lang-hi"
          className={`px-6 py-2.5 text-sm tracking-wide transition-colors border-l border-sans_line ${lang === 'hi' ? 'bg-sans_ink text-sans_bone' : 'bg-white text-sans_ink hover:bg-sans_bone'}`}>हिन्दी</button>
      </div>
      <div className="mt-2 text-xs text-sans_mute">Both the questions and your final report will appear in your chosen language.</div>
    </div>

    <div className="mt-12 grid sm:grid-cols-3 gap-6 text-sm">
      <div className="border border-sans_line p-6 bg-white"><div className="sans-label mb-2">Duration</div><div className="font-serif text-2xl text-sans_ink">~5 min</div></div>
      <div className="border border-sans_line p-6 bg-white"><div className="sans-label mb-2">Questions</div><div className="font-serif text-2xl text-sans_ink">15</div></div>
      <div className="border border-sans_line p-6 bg-white"><div className="sans-label mb-2">Cost</div><div className="font-serif text-2xl text-sans_ink">Free</div></div>
    </div>
    <button data-testid={ASSESSMENT.startBtn} onClick={onStart} disabled={loading}
      className="sans-btn-primary mt-14">{loading ? 'Preparing…' : 'Begin the assessment'}</button>
    <p className="mt-8 text-xs text-sans_mute leading-relaxed max-w-lg">
      Prakriti is only one part of a complete Ayurvedic assessment. This free report does not include personalised diet
      plans, medicine recommendations, or treatment protocols.
    </p>
  </div>
);

const Question = ({ q, progress, onAnswer, onBack, loading }) => {
  const pct = Math.round((progress.answered / progress.total) * 100);
  return (
    <div className="sans-container max-w-3xl sans-section">
      <div className="flex items-center justify-between mb-10">
        <button onClick={onBack} data-testid={ASSESSMENT.backBtn} className="text-sans_mute hover:text-sans_ink flex items-center gap-2 text-sm">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
        </button>
        <div data-testid={ASSESSMENT.progress} className="text-xs tracking-[0.22em] uppercase text-sans_mute">{progress.answered + 1} / {progress.total}</div>
      </div>
      <Progress value={pct} className="h-[1px] bg-sans_line" />
      <div className="mt-14">
        <div className="sans-label mb-4">{q.category}</div>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight text-sans_ink tracking-tight">{q.prompt}</h2>
      </div>
      <div className="mt-12 space-y-3">
        {q.options.map((o) => (
          <button key={o.id} onClick={() => onAnswer(o.id)} disabled={loading} data-testid={ASSESSMENT.optionBtn(o.id)}
            className="w-full text-left border border-sans_line bg-white hover:bg-sans_ink hover:text-white hover:border-sans_ink transition-all duration-300 p-6 group">
            <div className="flex items-center gap-5">
              <div className="w-6 h-6 border border-current rounded-full flex-shrink-0" />
              <div className="text-base md:text-lg leading-relaxed">{o.label}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const Assessment = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState('intro');
  const [assessmentId, setAssessmentId] = useState(null);
  const [question, setQuestion] = useState(null);
  const [progress, setProgress] = useState({ answered: 0, total: 15 });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState('en');
  const [concern, setConcern] = useState('');

  const start = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/assessment/start', { lang, concern: concern.trim() || null });
      setAssessmentId(data.assessment_id);
      setQuestion(data.question);
      setProgress(data.progress);
      setHistory([]);
      setState('quiz');
    } catch (e) { toast.error('Could not start assessment'); }
    finally { setLoading(false); }
  };

  const submitAnswer = async (optionId) => {
    if (!question || !assessmentId) return;
    const isLast = progress.answered + 1 >= progress.total;
    if (isLast) setState('submitting');
    setLoading(true);
    try {
      const { data } = await api.post('/assessment/answer', {
        assessment_id: assessmentId, question_id: question.id, option_id: optionId,
      });
      if (data.complete) {
        nav(`/assessment/${assessmentId}`, {
          replace: true,
          state: { status: 'generating', scores: data.scores, assessment_id: assessmentId },
        });
        return;
      }
      setHistory((h) => [...h, { q: question, optionId }]);
      setQuestion(data.question);
      setProgress(data.progress);
    } catch (e) {
      if (isLast) setState('quiz');
      toast.error(e?.response?.data?.detail || 'Something went wrong');
    } finally { setLoading(false); }
  };

  const back = async () => {
    if (history.length === 0 || !assessmentId) { setState('intro'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/assessment/back', { assessment_id: assessmentId });
      setHistory((h) => h.slice(0, -1));
      if (data.question) { setQuestion(data.question); setProgress(data.progress); }
      else { setState('intro'); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not go back'); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-sans_bone min-h-[calc(100vh-5rem)]">
      {state === 'intro' && <Intro onStart={start} loading={loading} lang={lang} setLang={setLang} concern={concern} setConcern={setConcern} />}
      {state === 'quiz' && question && (
        <Question q={question} progress={progress} onAnswer={submitAnswer} onBack={back} loading={loading} />
      )}
      {state === 'submitting' && (
        <div className="sans-container max-w-3xl sans-section text-center">
          <div className="mx-auto w-14 h-14 mb-8 relative">
            <div className="absolute inset-0 border-2 border-sans_line rounded-full" />
            <div className="absolute inset-0 border-2 border-transparent border-t-sans_ink rounded-full animate-spin" />
          </div>
          <div className="sans-label mb-6">Analysing your responses</div>
          <h2 className="sans-h2">Preparing your Prakriti report…</h2>
          <p className="mt-6 text-sans_mute max-w-md mx-auto leading-relaxed">
            Drawing on classical Ayurvedic principles to interpret your answers. This usually takes 10–20 seconds — please don't close this page.
          </p>
        </div>
      )}
    </div>
  );
};

export default Assessment;
